import { Alert, Box, Button, FormControl, FormLabel, Grid, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { LoadingBox, plural, useNavigateWithParams } from "@northpowerdev/np-react-utils";
import { useContext, useEffect } from "react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import useFetchJson from "../../../data/useFetchJson";
import { Controller, useForm } from "react-hook-form";
import { styled } from "@mui/system";
import { DebugBox } from "@northpowerdev/np-react-utils";
import { MahiContext, mahiTokenFromMahiContext } from "../../state/MahiContext";
import { GrayTextField } from "../../../utils/GrayTextField";


const LocalStyledH1 = styled('h1')(({theme}) => ({
    margin: 0
}));

export default function QRTagEdit() {

    const [tagId, setTagId] = useState(null)
    const mahiContext = useContext(MahiContext)
    const mahiTokenObject = mahiTokenFromMahiContext(mahiContext)

    const baseUrl = '/bff/isolation-tags-service/api/tag'
    const {
        load: loadTagDetails,
        isLoading: tagDetailsLoading,
        data: tagDetails,
    } = useFetchJson(baseUrl)

    const {
        load: updateTagDetails,
        isLoading: tagDetailsUpdating,
    } = useFetchJson(baseUrl, {method: 'POST'})

    const location = useLocation()
    const navigateToDetailsPage = useNavigateWithParams('/tag/details')

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const paramTagId = searchParams.get('tag_id')
        setTagId(paramTagId)
    }, [location])

    useEffect(() => {
        if (!tagId) {
            //
            // No tagId in url parameters. Don't try to load anything.
            //
            console.log('QRTagDetails useEffect DEBUG no tag id!')
            return
        }
        const fullUrl = baseUrl + '/' + tagId + '/read'
        // console.log('loading tag details in background from fullUrl', fullUrl)
        loadTagDetails({}, fullUrl) // run in background. Updates tagDetails
    // eslint-disable-next-line
    }, [tagId])

    const initialTagFormState = {
        tagId: '',
        tagStatus: '',
        isolationReason: '',
        assetId: '',
        assetType: 'ICP',
        updatedByName: mahiTokenObject.name,
        updatedByEmail: mahiTokenObject.email,
        updatedByPhone: mahiTokenObject.email || '',
        // updatedDate: '',
    }
    const { handleSubmit, control, reset, formState: { errors }, watch, clearErrors } = useForm({defaultValues: initialTagFormState})

    const watchStatus = watch('tagStatus')
    const watchUpdatedByName  = watch('updatedByName')
    const watchUpdatedByEmail = watch('updatedByEmail')
    const watchUpdatedByPhone = watch('updatedByPhone')

    useEffect(() => {
        //
        // Update form state when tag details are loaded.
        //
        if (tagDetails) {
            console.log('useEffect resetting form values with tagDetails', tagDetails)
            reset(dataFromTagDetails(tagDetails, mahiTokenObject))
        }
    // eslint-disable-next-line
    }, [tagDetails])

    const onSubmit = async (data) => {
        console.log('QRTagEdit onSubmit navigating back to details. Form data:', data)
        const setIsolatedOrIdle = (data.tagStatus === 'isolated' ? 'set-isolated' : 'set-idle')
        const fullUrl = baseUrl + '/' + tagId + '/' + setIsolatedOrIdle
        const postBody = tagPostBodyFromData(data)
        // console.log('updating tag postTagDetails', postBody)
        const postParams = {body: JSON.stringify(postBody)}
        const theUpdateTagResponse = await updateTagDetails(postParams, fullUrl)
        if (theUpdateTagResponse) {
            //
            // response is not undefined, maning there was no error during update
            //
            navigateToDetailsPage({tag_id: tagId})
        }
    }

    return (
        <Box className="Page">
            <h2 className="np-title" style={{marginBottom: 0}}>Edit Tag</h2>
            <Typography variant="body2" paragraph>Tag {tagId}</Typography>

            <LoadingBox loading={tagDetailsLoading} message={"Loading tag " + tagId}>

                <Grid container sx={{my:2}}>
                    <Grid item xs={12} md={3}></Grid>
                    <Grid item xs={12} md={6} textAlign="left">

                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Box sx={{display: 'flex', flexDirection: 'column' }}>

                                <FormControl sx={{my:2}}>
                                    <Controller name='tagStatus' control={control}
                                        rules={{required: true}}
                                        render={({field}) => ( // { onChange, onBlur, value, name, ref }
                                            <>
                                                <FormLabel color="secondary"><small>Tag Status *</small></FormLabel>
                                                <ToggleButtonGroup
                                                    color="primary"
                                                    size="large"
                                                    value={field.value}
                                                    exclusive
                                                    onChange={(e, newStatus) => { field.onChange(newStatus ? newStatus : field.value); clearErrors() }}
                                                >
                                                    <ToggleButton value="idle">
                                                        <LocalStyledH1>Idle</LocalStyledH1>
                                                    </ToggleButton>
                                                    <ToggleButton value="isolated">
                                                        <LocalStyledH1>Isolated</LocalStyledH1>
                                                    </ToggleButton>
                                                </ToggleButtonGroup>
                                            </>
                                        )}
                                    />
                                </FormControl>

                                {
                                    watchStatus === 'isolated'
                                    &&
                                    <OptionalFormControls control={control} errors={errors} />
                                }
                            </Box>

                            <Box sx={{my:2, display: 'flex', justifyContent: 'space-between'}}>
                                <Button type="submit" variant="contained" disabled={tagDetailsUpdating}>Save Changes</Button>
                            </Box>
                            {
                                Object.keys(errors).length > 0
                                &&
                                <Box textAlign="left">
                                    <Alert sx={{display: 'inline-flex'}} severity="error">We found {Object.keys(errors).length} error{plural(Object.keys(errors).length)}.</Alert>
                                </Box>
                            }

                            <Box sx={{my:2}}>
                                <Typography variant="body2">Update By:</Typography>
                                <Stack direction="row" sx={{gap: '10px'}}>
                                    <Typography variant="body2">{watchUpdatedByName}</Typography>
                                    <Typography variant="body2">({watchUpdatedByEmail})</Typography>
                                    <Typography variant="body2">{watchUpdatedByPhone}</Typography>
                                </Stack>
                            </Box>

                        </form>

                    </Grid>
                    <Grid item xs={12} md={3}></Grid>
                </Grid>

                <DebugBox title="tagDetails" stringify>{tagDetails}</DebugBox>
            </LoadingBox>
        </Box>
    )
}


function OptionalFormControls({control, errors}) {
    return <>
        <FormControl sx={{my:2}}>
            <Controller name='assetId' control={control}
                // rules={{required: true}}
                rules={{
                    validate: (value) => {
                        console.log('assetId value', value)
                        // const sixDigitsOk = value.length === 6 || value.match(/^[0-9]+$/)
                        const fifteenDigitsOk = value.length === 15 || value.match(/^[0-9A-FNR]+$/)
                        return (fifteenDigitsOk)
                    }
                }}
                render={({field, value}) => ( // { onChange, onBlur, value, name, ref }
                    <GrayTextField
                        sx={{my:1}}
                        label="ICP Number *"
                        value={field.value}
                        error={!!errors.assetId}
                        helperText={errors.assetId && "ICP Number must be exactly 15 digits, letters and numbers."}
                        onChange={(e) => { field.onChange(e.target.value); }}
                        />
                )}
            />
        </FormControl>

        <FormControl sx={{my:2}}>
            <Controller name='isolationReason' control={control}
                rules={{required: true}}
                render={({field}) => ( // { onChange, onBlur, value, name, ref }
                    <GrayTextField
                        sx={{my:1}}
                        label="Isolation Reason *"
                        value={field.value}
                        error={!!errors.isolationReason}
                        helperText={errors.isolationReason && "Isolation Reason is required"}
                        onChange={(e) => { field.onChange(e.target.value); }}
                        />
                )}
            />
        </FormControl>

    </>
}


function dataFromTagDetails(tagDetails, mahiTokenObject) {
    return {
        tagId: tagDetails.tagId || '',
        tagStatus: tagDetails.tagStatus || '',
        isolationReason: tagDetails.isolationReason || '',
        assetId: tagDetails.assetId || '',
        assetType: 'ICP',
        //
        // Ignore the updated by details we got from web service
        // and replace them with the current user's details.
        //
        updatedByName: mahiTokenObject.name,
        updatedByEmail: mahiTokenObject.email,
        updatedByPhone: mahiTokenObject.phone || ' ', // - should be optional field
        // updatedDate: tagDetails.updatedDate || '',
    }
}

function tagPostBodyFromData(data) {
    let tagUpdatedBy = {
        updatedBy: {
            name: data.updatedByName,
            email: data.updatedByEmail,
            phone: data.updatedByPhone || ' ' // Get rid of space when Dave makes this optional
        },
    }
    let otherTagDetails = {}
    if (data.tagStatus === 'isolated') {
        otherTagDetails = {
            isolationReason: data.isolationReason,
            assetType: data.assetType,
            assetId: data.assetId,
        }
    }
    return {...tagUpdatedBy, ...otherTagDetails}
}
